import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { ResetTimer } from '@/components/dashboard/ResetTimer'
import { OverflowUnlockTimer } from '@/components/dashboard/OverflowUnlockTimer'
import { Button } from '@/components/shared/Button'
import { useDarkMode } from '@/hooks/useDarkMode'

/** Admin quick-copy for the public affiliate portal link, for fast sharing. */
function CopyPortalLink({ className = '' }: { className?: string }) {
  const [copied, setCopied] = useState(false)
  const url = `${window.location.origin}/portal`
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      const el = document.createElement('input')
      el.value = url; document.body.appendChild(el); el.select(); document.execCommand('copy'); el.remove()
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }
  return (
    <button
      onClick={copy}
      title={`Copy affiliate portal link (${url})`}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${copied ? 'border-green-500 text-green-600 bg-green-50' : 'border-gray-300 text-text-secondary hover:text-text-primary hover:bg-gray-100'} ${className}`}
    >
      {copied ? (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
      )}
      {copied ? 'Copied!' : 'Portal link'}
    </button>
  )
}

export function Header() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const isAdmin = location.pathname.startsWith('/admin')
  // The cap day belongs to an OFFER, not to the app: `/` is the offer list, where no offer is
  // selected and a shell countdown could only state the UTC day — wrong for Reco and anything
  // else that rolls over elsewhere. On an offer's own page the live countdown sits next to the
  // offer name (`OfferResetTag`), so the shell doesn't repeat it; admin routes are network-wide
  // and keep the UTC-day timer.
  const isOfferList = location.pathname === '/'
  const isOfferDetail = location.pathname.startsWith('/offer/')
  const { isDark, toggle } = useDarkMode()
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <header className="bg-surface-card border-b border-border-subtle sticky top-0 z-40">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14">
          {/* Logo + Title */}
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-brand-blue rounded-lg flex items-center justify-center shadow-xs">
              <span className="font-bold text-xs text-white tracking-tight">M</span>
            </div>
            <div>
              <h1 className="font-semibold text-sm leading-none text-text-primary">
                Cap Manager
              </h1>
              <p className="text-[10px] text-text-muted tracking-wide uppercase mt-0.5">
                MABAC Media
              </p>
            </div>
          </div>

          {/* Center: overflow unlock + daily reset (hidden on mobile) */}
          {!isOfferList && (
            <div className="hidden md:flex items-center gap-2">
              {user?.user_type === 'affiliate' && <OverflowUnlockTimer />}
              {!isOfferDetail && <ResetTimer />}
            </div>
          )}

          {/* Right: Nav + User (desktop) */}
          <div className="hidden md:flex items-center gap-3">
            {user?.is_admin && <CopyPortalLink />}
            <button
              onClick={toggle}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-gray-100 transition-colors cursor-pointer"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            {user?.is_admin && (
              isAdmin ? (
                <Link to="/">
                  <Button variant="outline" className="!text-xs !px-3 !py-1.5">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/admin">
                  <Button variant="outline" className="!text-xs !px-3 !py-1.5">Admin</Button>
                </Link>
              )
            )}
            <div className="text-right">
              <p className="text-sm font-medium text-text-primary">
                {user?.name ?? 'Loading...'}
              </p>
            </div>
            <button
              onClick={logout}
              className="text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              Logout
            </button>
          </div>

          {/* Hamburger (mobile) */}
          <button
            onClick={() => setMobileOpen(o => !o)}
            className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-border-subtle mt-2 pt-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {!isOfferList && user?.user_type === 'affiliate' && <OverflowUnlockTimer />}
              {!isOfferList && !isOfferDetail && <ResetTimer />}
              <button
                onClick={toggle}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-gray-100 transition-colors cursor-pointer"
              >
                {isDark ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {user?.is_admin && <CopyPortalLink className="w-full justify-center" />}
              {user?.is_admin && (
                isAdmin ? (
                  <Link to="/" onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" className="!text-xs w-full">Dashboard</Button>
                  </Link>
                ) : (
                  <Link to="/admin" onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" className="!text-xs w-full">Admin</Button>
                  </Link>
                )
              )}
            </div>
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm font-medium text-text-primary">{user?.name ?? 'Loading...'}</p>
              <button onClick={logout} className="text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer">Logout</button>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
